import { useTraderTime } from '../../contexts/TraderTimeContext';
import React, { useState, useEffect, useRef } from 'react';
import { depositsAPI, TraderDepositResponse, TraderDepositList, getAppConfig, AppConfig, DepositLimits } from '../../lib/api';
import { balanceEvents, BALANCE_EVENTS } from '../../lib/events';
import { formatAmount, formatAmountWithSeparator } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CountdownTimer } from '../ui/countdown-timer';
import { QRCodeSVG } from 'qrcode.react';
import {
    Wallet,
    Plus,
    Copy,
    Check,
    AlertCircle,
    Loader2,
    RefreshCw,
    ExternalLink,
    Clock,
    CheckCircle,
    XCircle,
    Shield
} from 'lucide-react';

const sharedAmount = (value: number) => value.toLocaleString('en-US', { useGrouping: false, minimumFractionDigits: 1, maximumFractionDigits: 6 });

const depositAmount = (value: number, deposit: TraderDepositResponse) =>
    deposit.deposit_method === 'shared' ? `${sharedAmount(value)} USDT` : formatAmount(value, 'USDT');

interface CreateDepositModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (deposit: TraderDepositResponse) => void;
    shared?: boolean;
    platformFee?: number; // Передаем комиссию из родительского компонента
    maxDepositAmount?: number;
}

const CreateDepositModal: React.FC<CreateDepositModalProps> = ({ isOpen, onClose, onSuccess, platformFee = 3.0, maxDepositAmount, shared = false }) => {
    const [amount, setAmount] = useState<number | ''>('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const calculateCreditedAmount = () => {
        if (!amount || amount <= 0) return null;
        return Math.max(0, Number(amount) - platformFee);
    };

    const isAmountExceedsLimit = () => {
        if (!amount || !maxDepositAmount) return false;
        return Number(amount) > maxDepositAmount;
    };

    const handleCreate = async () => {
        if (!amount || amount <= 0) {
            setError('Введите корректную сумму');
            return;
        }

        if (shared && !Number.isInteger(Number(amount))) {
            setError('Введите целую сумму USDT, без дробной части');
            return;
        }

        if (maxDepositAmount !== undefined && Number(amount) > maxDepositAmount) {
            setError(`Сумма депозита не может превышать допустимый лимит (${formatAmount(maxDepositAmount, 'USDT')})`);
            return;
        }

        try {
            setIsCreating(true);
            setError(null);
            const deposit = await depositsAPI.createDeposit({
                expected_amount: Number(amount)
            });
            onSuccess(deposit);
            onClose();
        } catch (err: any) {
            setError('Ошибка создания депозита: ' + (err.response?.data?.detail || err.message));
        } finally {
            setIsCreating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        Создать депозит
                    </CardTitle>
                    <CardDescription>
                        Создайте новый депозит для пополнения баланса USDT
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {maxDepositAmount !== undefined && (
                        <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
                            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                                <Shield className="h-4 w-4" />
                                <span className="text-sm font-medium">
                                    Максимальная сумма депозита: {formatAmount(maxDepositAmount, 'USDT')}
                                </span>
                            </div>
                            <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                                Сумма депозита ограничена разницей между страховым балансом и текущим балансом
                            </p>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="amount">Ожидаемая сумма USDT</Label>
                        <Input
                            id="amount"
                            type="number"
                            step={shared ? "1" : "0.01"}
                            min="1"
                            max={maxDepositAmount}
                            placeholder={shared ? "100" : "100.00"}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : '')}
                            className={isAmountExceedsLimit() ? 'border-red-500' : ''}
                        />
                        {isAmountExceedsLimit() && (
                            <p className="text-xs text-red-600 dark:text-red-300">
                                Сумма превышает максимально допустимую ({formatAmount(maxDepositAmount!, 'USDT')})
                            </p>
                        )}
                        {!isAmountExceedsLimit() && (
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                {shared ? "Укажите целое число USDT — без дробной части" : "Укажите точную сумму"}
                            </p>
                        )}
                    </div>

                    {/* Информация о комиссии */}
                    {amount && Number(amount) > 0 && !isAmountExceedsLimit() && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-300">{shared ? "Исходная сумма:" : "Вы отправляете:"}</span>
                                <span className="font-medium">{formatAmount(Number(amount), 'USDT')}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-300">Комиссия платформы:</span>
                                <span className="font-medium text-orange-600 dark:text-orange-300">{formatAmount(platformFee, 'USDT')}</span>
                            </div>
                            <div className="border-t pt-2 flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-300 font-medium">{shared ? "До добавки:" : "Будет зачислено:"}</span>
                                <span className="font-bold text-green-600 dark:text-green-300">
                                    {formatAmount(calculateCreditedAmount() || 0, 'USDT')}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                                {shared ? "После создания добавим случайную сумму от 0.1 до 0.9 USDT. Переведите точную сумму одним платежом за 30 минут — зачислим её целиком." : "С каждого депозита удерживается комиссия платформы"}
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-300 rounded-md">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    <div className="flex gap-2">
                        <Button
                            onClick={handleCreate}
                            disabled={isCreating || isAmountExceedsLimit()}
                            className="flex-1"
                        >
                            {isCreating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Создаём...
                                </>
                            ) : (
                                'Создать депозит'
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={isCreating}
                        >
                            Отмена
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

interface DepositDetailsModalProps {
    deposit: TraderDepositResponse | null;
    onClose: () => void;
    onStatusUpdate: () => void;
    onConfirmSuccess?: () => void;
    getExplorerUrl: (address: string) => string;
    appConfig: AppConfig | null;
}

const DepositDetailsModal: React.FC<DepositDetailsModalProps> = ({
    deposit,
    onClose,
    onStatusUpdate,
    onConfirmSuccess,
    getExplorerUrl,
    appConfig
}) => {
    const { formatDate } = useTraderTime();
    const [copied, setCopied] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const handleConfirm = async () => {
        if (!deposit) return;

        try {
            setIsConfirming(true);
            setConfirmError(null);
            const result = await depositsAPI.confirmDeposit({
                deposit_id: deposit.id,
            });

            // Обновляем список депозитов
            await onStatusUpdate();

            // Вызываем callback для показа сообщения об успехе
            if (onConfirmSuccess) {
                onConfirmSuccess();
            }

            // Закрываем модальное окно
            onClose();
        } catch (err: any) {
            console.error('Ошибка подтверждения:', err);
            setConfirmError(err.response?.data?.detail || err.message || 'Неизвестная ошибка');
        } finally {
            setIsConfirming(false);
        }
    };

    const handleCheckBalance = async () => {
        if (!deposit) return;

        try {
            setIsChecking(true);
            await depositsAPI.checkDepositBalance(deposit.id);
            // Сначала обновляем список депозитов
            await onStatusUpdate();
        } catch (err: any) {
            console.error('Ошибка проверки:', err);
        } finally {
            setIsChecking(false);
        }
    };


    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock className="h-4 w-4" />;
            case 'received':
            case 'processing':
                return <Loader2 className="h-4 w-4 animate-spin" />;
            case 'completed':
                return <CheckCircle className="h-4 w-4" />;
            case 'failed':
                return <XCircle className="h-4 w-4" />;
            default:
                return <Clock className="h-4 w-4" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return 'Ожидает поступления';
            case 'received':
                return 'Средства получены';
            case 'processing':
                return 'Обрабатывается';
            case 'completed':
                return 'Завершён';
            case 'treasury_sent':
                return 'Завершён';
            case 'failed':
                return 'Ошибка';
            case 'expired':
                return 'Срок оплаты истёк';
            case 'cancelled':
                return 'Отменён';
            default:
                return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-200';
            case 'received':
            case 'processing':
                return 'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200';
            case 'completed':
            case 'treasury_sent':
                return 'bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-200';
            case 'failed':
                return 'bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200';
            case 'cancelled':
                return 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-100';
            default:
                return 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-100';
        }
    };

    if (!deposit) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Wallet className="h-5 w-5" />
                        Детали депозита
                    </CardTitle>
                    <CardDescription>
                        ID: {deposit.id}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Статус */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Статус:</span>
                        <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(deposit.status)}>
                                {getStatusIcon(deposit.status)}
                                <span className="ml-1">{getStatusText(deposit.status)}</span>
                            </Badge>
                            {deposit.user_confirmed && deposit.status === 'pending' && (
                                <Badge className="bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Подтверждено
                                </Badge>
                            )}
                        </div>
                    </div>

                    {/* Адрес для депозита с QR-кодом */}
                    <div className="space-y-4">
                        <Label className="text-sm font-medium">Адрес для депозита USDT TRC-20:</Label>

                        {/* QR-код */}
                        <div className="flex flex-col items-center space-y-3 p-4 bg-white rounded-lg border">
                            <QRCodeSVG
                                value={deposit.deposit_address}
                                size={200}
                                level="M"
                                includeMargin={true}
                                className="border rounded"
                            />
                            <p className="text-xs text-gray-600 dark:text-slate-300 text-center max-w-xs">
                                Отсканируйте QR-код в вашем криптокошельке для быстрого ввода адреса
                            </p>
                        </div>

                        {/* Текстовый адрес */}
                        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-900 rounded-md">
                            <code className="flex-1 text-sm break-all">
                                {deposit.deposit_address}
                            </code>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyToClipboard(deposit.deposit_address)}
                            >
                                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <p className="text-xs text-red-600 dark:text-red-300 font-medium">
                                ⚠️ Отправляйте только USDT TRC-20 на этот адрес!
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                💡 Используйте QR-код для быстрого и точного ввода адреса в кошельке
                            </p>
                        </div>
                    </div>


                    {deposit.deposit_method === 'shared' && (
                        <div className="rounded-md border border-blue-300 p-4 space-y-2">
                            <p className="font-medium">{deposit.network === 'mainnet' ? 'Tron Mainnet' : `${deposit.network} — тестовая сеть`} · USDT TRC-20</p>
                            {deposit.status === 'pending' && <>
                                <p>Переведите одним платежом точно:</p>
                                <div className="flex items-center gap-3">
                                    <strong className="text-2xl">{deposit.expected_amount == null ? '—' : sharedAmount(deposit.expected_amount)} USDT</strong>
                                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(sharedAmount(deposit.expected_amount!))}>Копировать сумму</Button>
                                </div>
                                {deposit.expires_at && <CountdownTimer expiresAt={deposit.expires_at} serverTime={deposit.server_time} />}
                                <p className="text-sm">Не округляйте сумму. Комиссия отправителя не должна уменьшить поступление. QR-код содержит только адрес.</p>
                            </>}
                            {['expired', 'cancelled'].includes(deposit.status) && <p>Не оплачивайте эту заявку. Если уже отправили средства, обратитесь в поддержку с TX hash.</p>}
                            <p className="text-sm">Зачисление — после подтверждения перевода в сети. Если сумма отличается, обратитесь в поддержку с TX hash.</p>
                            {deposit.receive_tx_hash && <p className="text-xs break-all">TX: {deposit.receive_tx_hash}</p>}
                            {deposit.error_message && <p className="text-amber-700">{deposit.error_message}</p>}
                        </div>
                    )}

                    {/* Суммы */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            {deposit.expected_amount && (
                                <div className="space-y-1">
                                    <Label className="text-sm text-gray-600 dark:text-slate-300">Ожидаемая сумма:</Label>
                                    <div className="text-lg font-medium">
                                        {depositAmount(deposit.expected_amount, deposit)}
                                    </div>
                                </div>
                            )}
                            {deposit.received_amount && (
                                <div className="space-y-1">
                                    <Label className="text-sm text-gray-600 dark:text-slate-300">Получено:</Label>
                                    <div className="text-lg font-medium text-blue-600 dark:text-blue-300">
                                        {depositAmount(deposit.received_amount, deposit)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Информация о комиссии и зачислении */}
                        {(deposit.platform_fee !== undefined || deposit.expected_credited_amount || deposit.credited_amount) && (
                            <div className="p-3 bg-gray-50 dark:bg-slate-900 rounded-md space-y-2">
                                {deposit.platform_fee !== undefined && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-slate-300">Комиссия платформы:</span>
                                        <span className="font-medium text-orange-600 dark:text-orange-300">
                                            -{formatAmount(Math.abs(deposit.platform_fee), 'USDT')}
                                        </span>
                                    </div>
                                )}
                                {deposit.expected_credited_amount && deposit.status === 'pending' && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-slate-300">Будет зачислено:</span>
                                        <span className="font-medium text-green-600 dark:text-green-300">
                                            {depositAmount(deposit.expected_credited_amount, deposit)}
                                        </span>
                                    </div>
                                )}
                                {deposit.credited_amount !== undefined && deposit.status === 'completed' && (
                                    <div className="flex justify-between text-sm border-t pt-2">
                                        <span className="text-gray-600 dark:text-slate-300 font-medium">Зачислено на баланс:</span>
                                        <span className="font-bold text-green-600 dark:text-green-300">
                                            +{depositAmount(deposit.credited_amount, deposit)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Дата создания */}
                    <div className="space-y-1">
                        <Label className="text-sm text-gray-600 dark:text-slate-300">Создан:</Label>
                        <div className="text-sm">
                            {formatDate(deposit.created_at)}
                        </div>
                    </div>

                    {/* Действия для ожидающих депозитов */}
                    {deposit.status === 'pending' && (
                        <div className="space-y-4">
                            {deposit.user_confirmed ? (
                                <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                                        <CheckCircle className="h-5 w-5" />
                                        <div>
                                            <p className="font-medium">Депозит подтвержден</p>
                                            <p className="text-sm text-blue-600 dark:text-blue-300">
                                                Ожидаем поступления средств. Проверка баланса выполняется автоматически каждые несколько минут.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <Button
                                            variant="outline"
                                            onClick={handleCheckBalance}
                                            disabled={isChecking}
                                            size="sm"
                                        >
                                            {isChecking ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    Проверяем...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                    Проверить сейчас
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Ошибка подтверждения */}
                                    {confirmError && (
                                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-300 rounded-md border border-red-200 dark:border-red-800">
                                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                            <span className="text-sm">{confirmError}</span>
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleConfirm}
                                            disabled={isConfirming}
                                            className="flex-1"
                                        >
                                            {isConfirming ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Подтверждаем...
                                                </>
                                            ) : (
                                                'Подтвердить отправку'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={handleCheckBalance}
                                            disabled={isChecking}
                                        >
                                            {isChecking ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}


                    {/* Ссылка на блокчейн эксплорер */}
                    <div className="space-y-2">
                        <a
                            href={deposit.network ? `https://${deposit.network === "mainnet" ? "" : deposit.network + "."}tronscan.org/address/${deposit.deposit_address}` : getExplorerUrl(deposit.deposit_address)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 text-sm"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Посмотреть в блокчейн эксплорере
                        </a>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            {appConfig?.is_production ? '🌍 Mainnet - реальные средства' : `🧪 ${appConfig?.tron_network?.toUpperCase()} Testnet - для тестирования`}
                        </p>
                    </div>

                    <div className="flex justify-end">
                        <Button variant="outline" onClick={onClose}>
                            Закрыть
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export const DepositBalance: React.FC = () => {
    const { formatDate } = useTraderTime();
    const [deposits, setDeposits] = useState<TraderDepositList | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState<TraderDepositResponse | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
    const [platformFee, setPlatformFee] = useState<number>(3.0); // Комиссия платформы
    const [depositLimits, setDepositLimits] = useState<DepositLimits | null>(null); //лимиты депозита

    // Загрузка списка депозитов
    const fetchDeposits = async (silent = false) => {
        try {
            if (!silent) setIsLoading(true);
            setError(null);
            const data = await depositsAPI.getDepositsList();
            if (silent && data.deposits.some(d => d.deposit_method === 'shared' && d.status === 'completed'
                && deposits?.deposits.some(old => old.id === d.id && old.status !== 'completed'))) {
                balanceEvents.emit(BALANCE_EVENTS.DEPOSIT_COMPLETED);
            }
            setDeposits(data);


        } catch (err: any) {
            setError('Ошибка загрузки депозитов: ' + (err.response?.data?.detail || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSuccess = (deposit: TraderDepositResponse) => {
        setSelectedDeposit(deposit);
        fetchDeposits();
        fetchDepositLimits();

        // Уведомляем об изменении баланса если депозит завершён
        if (deposit.status === 'completed') {
            balanceEvents.emit(BALANCE_EVENTS.DEPOSIT_COMPLETED);
        }
    };

    const handleStatusUpdate = () => {
        fetchDeposits();

        if (selectedDeposit) {
            depositsAPI.getDeposit(selectedDeposit.id)
                .then(setSelectedDeposit)
                .catch(console.error);
        }
    };

    const handleConfirmSuccess = () => {
        setSuccessMessage('✅ Депозит подтвержден! Ожидайте поступления средств.');
        setTimeout(() => setSuccessMessage(null), 5000);
    };

    const handleCancelDeposit = async (depositId: string) => {
        setIsCancelling(true);
        try {
            await depositsAPI.cancelDeposit(depositId);

            await fetchDeposits();
            await fetchDepositLimits();

            // Закрываем модальное окно если депозит был отменен из модального окна
            if (selectedDeposit?.id === depositId) {
                setSelectedDeposit(null);
            }

            // Уведомляем об успехе
            setSuccessMessage('Депозит успешно отменен');
            setTimeout(() => setSuccessMessage(null), 3000);

        } catch (error: any) {
            console.error('Ошибка отмены депозита:', error);
            setError(error.response?.data?.detail || 'Ошибка при отмене депозита');
        } finally {
            setIsCancelling(false);
        }
    };

    // Загрузка конфигурации приложения
    const fetchAppConfig = async () => {
        try {
            const config = await getAppConfig();
            setAppConfig(config);
        } catch (error) {
            console.error('Ошибка загрузки конфигурации:', error);
        }
    };

    const fetchDepositLimits = async () => {
        try {
            const limits = await depositsAPI.getLimits();
            setDepositLimits(limits);
            setPlatformFee(limits.platform_fee ?? 3.0);
        } catch (error) {
            console.error('Ошибка загрузки лимитов депозита:', error);
        }
    };

    // Генерация ссылки на блокчейн explorer
    const getExplorerUrl = (address: string) => {
        if (!appConfig) return '#';

        const network = appConfig.tron_network;
        if (network === 'mainnet') {
            return `https://tronscan.org/address/${address}`;
        } else if (network === 'nile') {
            return `https://nile.tronscan.org/address/${address}`;
        } else {
            // shasta или другие testnet
            return `https://shasta.tronscan.org/address/${address}`;
        }
    };

    // Получение описания сети
    const getNetworkDescription = () => {
        if (!appConfig) return 'Загрузка...';

        if (appConfig.is_production) {
            return 'USDT TRC-20 на основной сети Tron';
        } else {
            const networkName = appConfig.tron_network === 'nile' ? 'Nile' :
                appConfig.tron_network === 'shasta' ? 'Shasta' :
                    appConfig.tron_network;
            return `USDT TRC-20 на тестовой сети ${networkName}`;
        }
    };

    useEffect(() => {
        fetchDeposits();
        fetchAppConfig();
        fetchDepositLimits();
    }, []);

    const refreshShared = useRef<() => Promise<void>>(async () => {});
    refreshShared.current = async () => {
        try {
            await fetchDeposits(true);
            await fetchDepositLimits();
            if (selectedDeposit?.deposit_method === 'shared') {
                setSelectedDeposit(await depositsAPI.getDeposit(selectedDeposit.id));
            }
        } catch (err) {
            console.error('Ошибка обновления депозита', err);
        }
    };
    const hasSharedDeposits = deposits?.deposits.some(d => d.deposit_method === 'shared');
    useEffect(() => {
        if (!hasSharedDeposits) return;
        const timer = window.setInterval(() => { void refreshShared.current(); }, 15000);
        return () => window.clearInterval(timer);
    }, [hasSharedDeposits]);

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Wallet className="h-5 w-5" />
                        Пополнение баланса
                    </CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center items-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Загружаем депозиты...</span>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Wallet className="h-5 w-5" />
                        Пополнение баланса USDT
                    </CardTitle>
                    <CardDescription>
                        Пополните баланс через {getNetworkDescription()}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Статистика */}
                    {deposits && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-md">
                                <div className="text-sm text-blue-600 dark:text-blue-300 font-medium">Всего депозитов</div>
                                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{deposits.total}</div>
                            </div>
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-md">
                                <div className="text-sm text-yellow-600 dark:text-yellow-300 font-medium">Ожидают</div>
                                <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{deposits.pending_count}</div>
                            </div>
                            <div className="p-4 bg-green-50 dark:bg-green-950 rounded-md">
                                <div className="text-sm text-green-600 dark:text-green-300 font-medium">Получено всего</div>
                                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                                    {formatAmount(deposits.total_received, 'USDT')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Кнопка создания депозита */}
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium">Мои депозиты</h3>
                        <div className="flex gap-2">
                            <Button
                                onClick={async () => { await fetchDepositLimits(); setShowCreateModal(true); }}
                                className="flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Создать депозит
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => fetchDeposits()}
                                disabled={isLoading}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Ошибка */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-300 rounded-md">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {/* Сообщение об успехе */}
                    {successMessage && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-300 rounded-md">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm">{successMessage}</span>
                        </div>
                    )}

                    {/* Список депозитов */}
                    {deposits && deposits.deposits.length > 0 ? (
                        <div className="space-y-3">
                            {deposits.deposits.map((deposit) => (
                                <div
                                    key={deposit.id}
                                    className="flex items-center justify-between p-4 border rounded-md hover:bg-gray-50 dark:hover:bg-slate-900"
                                >
                                    <div
                                        className="flex items-center gap-3 flex-1 cursor-pointer"
                                        onClick={() => setSelectedDeposit(deposit)}
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <Badge className={getStatusColor(deposit.status)}>
                                                    {getStatusText(deposit.status)}
                                                </Badge>
                                                {deposit.expected_amount && (
                                                    <span className="text-sm text-gray-600 dark:text-slate-300">
                                                        {depositAmount(deposit.expected_amount, deposit)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-slate-400">
                                                {formatDate(deposit.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            {(deposit.status === 'completed' || deposit.status === 'treasury_sent') && deposit.credited_amount ? (
                                                <div>
                                                    <div className="text-lg font-medium text-green-600 dark:text-green-300">
                                                        +{depositAmount(deposit.credited_amount, deposit)}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-slate-400">
                                                        зачислено
                                                    </div>
                                                </div>
                                            ) : deposit.received_amount ? (
                                                <div className="text-lg font-medium text-blue-600 dark:text-blue-300">
                                                    {depositAmount(deposit.received_amount, deposit)}
                                                </div>
                                            ) : null}
                                            <div className="text-xs text-gray-500 dark:text-slate-400">
                                                {deposit.deposit_address.slice(0, 8)}...
                                            </div>
                                        </div>
                                        {deposit.status === 'pending' && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCancelDeposit(deposit.id);
                                                }}
                                                disabled={isCancelling}
                                                className="text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                                            >
                                                {isCancelling ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    'Отменить'
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : deposits && (
                        <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                            <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>У вас пока нет депозитов</p>
                            <p className="text-sm">Создайте первый депозит для пополнения баланса</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Модальные окна */}
            <CreateDepositModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
                shared={depositLimits?.deposit_method === "shared"}
                platformFee={platformFee}
                maxDepositAmount={depositLimits?.max_deposit_amount}
            />

            <DepositDetailsModal
                deposit={selectedDeposit}
                onClose={() => setSelectedDeposit(null)}
                onStatusUpdate={handleStatusUpdate}
                onConfirmSuccess={handleConfirmSuccess}
                getExplorerUrl={getExplorerUrl}
                appConfig={appConfig}
            />
        </>
    );
};

// Вспомогательные функции (дублируются из DepositDetailsModal для использования в списке)
const getStatusText = (status: string) => {
    switch (status) {
        case 'pending':
            return 'Ожидает';
        case 'received':
            return 'Получено';
        case 'processing':
            return 'Обработка';
        case 'completed':
            return 'Завершён';
        case 'treasury_sent':
            return 'Завершён';
        case 'failed':
            return 'Ошибка';
        case 'expired':
            return 'Срок оплаты истёк';
        case 'cancelled':
            return 'Отменён';
        default:
            return status;
    }
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'pending':
            return 'bg-yellow-100 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-200';
        case 'received':
        case 'processing':
            return 'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200';
        case 'completed':
        case 'treasury_sent':
            return 'bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-200';
        case 'failed':
            return 'bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200';
        case 'cancelled':
            return 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-100';
        default:
            return 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-100';
    }
};
